/* ============================================================
   Trasse Families — the household's runs, read once and shared.

   Analytics and Reports are two views of ONE query, so they live behind one
   hook. A household has exactly one car, which is what makes this cheap: the
   proven school query shape (`where busId ==`, `orderBy routeStartedAt desc`,
   bounded `limit`) needs no new index and — because every returned document
   resolves the SAME `buses/{busId}` path — costs one cached rule lookup rather
   than one per row.

   ⚠️ What this deliberately does NOT return, because nothing writes it yet:

     • FUEL. `familyRefuelEvents` has rules and no writer anywhere in the tree —
       not mobile, not the backend, not the dashboard. Any "spend per week"
       figure built here today would be a number with nothing behind it.
     • DRIVING QUALITY. There is no scorecard collection at all. Harsh braking,
       speed and idling are not recorded for anyone, let alone a household.

   Both are named on screen as not-yet rather than rendered as zero. A zero and
   an absence look identical in a chart, and only one of them is true.
   ============================================================ */
import { useEffect, useState } from 'react';
import { collection, getDocs, limit, onSnapshot, orderBy, query, where, doc } from 'firebase/firestore';
import { db } from '../../firebase';

/** Bounded on purpose: a term is ~13 weeks × 10 runs. 260 covers two terms and
 *  caps what a single household can ever pull down in one go. */
export const RUNS_LIMIT = 260;

export interface FamilyRun {
  id: string;
  /** 'morning' | 'evening' — the leg. */
  routeType: string;
  startedAt: Date | null;
  finishedAt: Date | null;
  durationMinutes: number | null;
  distanceKm: number | null;
  /** 'gps_odometer' | 'route_points' | null — how the distance was arrived at. */
  distanceKmSource: string | null;
  status: string;
  /** 'parent' | 'driver' — who was at the wheel. */
  driverRole: string;
  /** True when the run recorded no distance at all. Shown as "not recorded",
   *  never as a zero — a zero reads as "the car did not move". */
  distanceMissing: boolean;
}

export interface FamilySchedule {
  /** weekly[dayKey][leg].pickupAt — the time the run was expected to start. */
  weekly: Record<string, Record<string, { enabled?: boolean; pickupAt?: string }>>;
  breaks: { from: string; to: string; label: string }[];
}

const DAY_KEYS = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'];

function toDate(v: unknown): Date | null {
  if (!v) return null;
  if (typeof (v as { toDate?: () => Date }).toDate === 'function') return (v as { toDate: () => Date }).toDate();
  if (v instanceof Date) return v;
  return null;
}

function num(v: unknown): number | null {
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

export function useFamilyRuns(busId: string, orgCode: string) {
  const [runs, setRuns] = useState<FamilyRun[]>([]);
  const [schedule, setSchedule] = useState<FamilySchedule>({ weekly: {}, breaks: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    let alive = true;
    if (!busId) { setLoading(false); setRuns([]); return; }
    (async () => {
      try {
        const snap = await getDocs(query(
          collection(db, 'busAttendance'),
          where('busId', '==', busId),
          orderBy('routeStartedAt', 'desc'),
          limit(RUNS_LIMIT),
        ));
        if (!alive) return;
        const rows: FamilyRun[] = [];
        snap.forEach((d) => {
          const x = d.data() as Record<string, unknown>;
          const km = num(x.distanceKm);
          rows.push({
            id: d.id,
            routeType: String(x.routeType || ''),
            startedAt: toDate(x.routeStartedAt) || toDate(x.date),
            finishedAt: toDate(x.routeFinishedAt),
            durationMinutes: num(x.durationMinutes),
            distanceKm: km,
            distanceKmSource: x.distanceKmSource ? String(x.distanceKmSource) : null,
            status: String(x.status || ''),
            driverRole: String(x.familyDriverRole || ''),
            distanceMissing: km === null || km <= 0,
          });
        });
        setRuns(rows);
        setLoading(false);
      } catch {
        if (alive) { setError(true); setLoading(false); }
      }
    })();
    return () => { alive = false; };
  }, [busId]);

  useEffect(() => {
    if (!orgCode) return;
    const unsub = onSnapshot(doc(db, 'familySchedules', orgCode), (snap) => {
      const d = snap.data() || {};
      setSchedule({
        weekly: (d.weekly || {}) as FamilySchedule['weekly'],
        breaks: Array.isArray(d.breaks) ? (d.breaks as FamilySchedule['breaks']) : [],
      });
    }, () => { /* the schedule is optional context, never the reason a page fails */ });
    return () => unsub();
  }, [orgCode]);

  return { runs, schedule, loading, error };
}

/** The time this run was supposed to start, from the household's own timetable.
 *  Null when the day or leg carries no time — which is not lateness, it is a
 *  run that was never scheduled (a Saturday trip, a rescheduled term). */
export function scheduledStart(run: FamilyRun, schedule: FamilySchedule): Date | null {
  if (!run.startedAt) return null;
  const dayKey = DAY_KEYS[run.startedAt.getDay()];
  const leg = run.routeType === 'evening' ? 'evening' : 'morning';
  const cfg = schedule.weekly?.[dayKey]?.[leg];
  const hhmm = String(cfg?.pickupAt || '');
  const m = /^([01][0-9]|2[0-3]):([0-5][0-9])$/.exec(hhmm);
  if (!m) return null;
  const d = new Date(run.startedAt);
  d.setHours(Number(m[1]), Number(m[2]), 0, 0);
  return d;
}

/** Minutes late, or null when there is nothing to compare against. Negative
 *  means early — kept signed on purpose, because "always eight minutes early"
 *  is as much a fact about a household as being late. */
export function minutesLate(run: FamilyRun, schedule: FamilySchedule): number | null {
  const planned = scheduledStart(run, schedule);
  if (!planned || !run.startedAt) return null;
  return Math.round((run.startedAt.getTime() - planned.getTime()) / 60000);
}

export interface RunTotals {
  count: number;
  completed: number;
  km: number;
  /** Runs whose distance was never recorded — reported, never folded into km. */
  kmMissing: number;
  minutes: number;
  minutesCount: number;
  parentDrove: number;
  driverDrove: number;
}

export function totalsFor(runs: FamilyRun[]): RunTotals {
  const t: RunTotals = {
    count: 0, completed: 0, km: 0, kmMissing: 0, minutes: 0, minutesCount: 0,
    parentDrove: 0, driverDrove: 0,
  };
  for (const r of runs) {
    t.count += 1;
    if (r.status === 'completed') t.completed += 1;
    if (r.distanceMissing) t.kmMissing += 1;
    else t.km += r.distanceKm || 0;
    if (r.durationMinutes != null && r.durationMinutes > 0) {
      t.minutes += r.durationMinutes;
      t.minutesCount += 1;
    }
    if (r.driverRole === 'parent') t.parentDrove += 1;
    else if (r.driverRole === 'driver') t.driverDrove += 1;
  }
  return t;
}

/** Group runs into calendar weeks, newest first. Used by both surfaces. */
export function byWeek(runs: FamilyRun[]): { weekStart: Date; runs: FamilyRun[] }[] {
  const buckets = new Map<number, { weekStart: Date; runs: FamilyRun[] }>();
  for (const r of runs) {
    if (!r.startedAt) continue;
    const d = new Date(r.startedAt);
    d.setHours(0, 0, 0, 0);
    // Monday-first, matching how the Hours page reads the week.
    const shift = (d.getDay() + 6) % 7;
    d.setDate(d.getDate() - shift);
    const key = d.getTime();
    if (!buckets.has(key)) buckets.set(key, { weekStart: d, runs: [] });
    buckets.get(key)!.runs.push(r);
  }
  return [...buckets.values()].sort((a, b) => b.weekStart.getTime() - a.weekStart.getTime());
}

/** Runs inside [from, to], inclusive of both days. */
export function runsBetween(runs: FamilyRun[], from: Date, to: Date): FamilyRun[] {
  const lo = new Date(from); lo.setHours(0, 0, 0, 0);
  const hi = new Date(to); hi.setHours(23, 59, 59, 999);
  return runs.filter((r) => r.startedAt && r.startedAt >= lo && r.startedAt <= hi);
}
